import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession, hasAnyPermission, type SessionState } from '@/shared/api/session';
import { Skeleton } from '@/shared/ui';

/** Ruta a la que debe ir una sesión según su estado (§6.2): MFA → cambio de contraseña → app. */
export function routeForState(state: SessionState | undefined): string | null {
  switch (state) {
    case 'mfa_enrollment_required': return '/auth/mfa/enroll';
    case 'mfa_pending': return '/auth/mfa';
    case 'password_change_required': return '/auth/change-password';
    default: return null;
  }
}

function Loading() {
  return <div style={{ padding: 'var(--sp-8)', maxWidth: 480 }}><Skeleton height={24} /><div style={{ height: 12 }} /><Skeleton height={160} /></div>;
}

/** Exige sesión plena (state=active). Redirige a login conservando la URL de retorno. */
export function RequireAuth() {
  const { data, isPending } = useSession();
  const loc = useLocation();
  if (isPending) return <Loading />;
  if (!data) return <Navigate to="/auth/login" state={{ from: loc.pathname + loc.search }} replace />;
  const r = routeForState(data.state);
  if (r) return <Navigate to={r} replace />;
  return <Outlet />;
}

/** Para pantallas intermedias (MFA, cambio de contraseña): requiere sesión en el estado indicado. */
export function RequireState({ states }: { states: SessionState[] }) {
  const { data, isPending } = useSession();
  if (isPending) return <Loading />;
  if (!data) return <Navigate to="/auth/login" replace />;
  if (!states.includes(data.state)) return <Navigate to={routeForState(data.state) ?? '/'} replace />;
  return <Outlet />;
}

export function RequirePermission({ anyOf }: { anyOf: string[] }) {
  const { data } = useSession();
  if (!hasAnyPermission(data, anyOf)) return <Navigate to="/403" replace />;
  return <Outlet />;
}

/** Login cuando ya hay sesión: redirige. */
export function RedirectIfAuthed() {
  const { data, isPending } = useSession();
  if (isPending) return <Loading />;
  if (data) return <Navigate to={routeForState(data.state) ?? '/'} replace />;
  return <Outlet />;
}
